export type WorkspacePortalMode = 'job' | 'project' | 'event'

export type WorkspaceLabels = {
  mode: WorkspacePortalMode
  entity: string
  entityPlural: string
  entityLower: string
  entityPluralLower: string
  basePath: string
  workDaysBasePath: string
  workspaceTitle: string
  workDay: string
  workDayPlural: string
  workDayLower: string
  workDayPluralLower: string
  workDayEntry: string
  workDayEntryPlural: string
  workDayEntryLower: string
  workDayEntryPluralLower: string
  scheduleBadge: string
  scheduleScopedDescription: string
  workDaysBrowseDescription: string
  workDayCalendarError: string
  configurationManager: string
  unknownEntity: string
  galleryTitle: string
  deleteConfirmationPhrase: string
  entityIdLabel: string
  createdEntityLegend: string
  recurringFromEntity: string
}

const JOB_LABELS: WorkspaceLabels = {
  mode: 'job',
  entity: 'Job',
  entityPlural: 'Jobs',
  entityLower: 'job',
  entityPluralLower: 'jobs',
  basePath: '/job',
  workDaysBasePath: '/workdays',
  workspaceTitle: 'Jobs Workspace',
  workDay: 'Work Day',
  workDayPlural: 'Work Days',
  workDayLower: 'work day',
  workDayPluralLower: 'work days',
  workDayEntry: 'Work Day Entry',
  workDayEntryPlural: 'Work Day Entries',
  workDayEntryLower: 'work day entry',
  workDayEntryPluralLower: 'work day entries',
  scheduleBadge: 'Job schedule',
  scheduleScopedDescription: 'Pick a date on the calendar, add worker entries, and review the day schedule for this job.',
  workDaysBrowseDescription: 'Browse work days on the calendar first, then select a date to load worker entries for that day.',
  workDayCalendarError: 'Failed to load work day calendar',
  configurationManager: 'Job Configuration Manager',
  unknownEntity: 'Unknown job',
  galleryTitle: 'Job Gallery',
  deleteConfirmationPhrase: 'delete job',
  entityIdLabel: 'Job ID',
  createdEntityLegend: 'Created job',
  recurringFromEntity: 'Generated from recurring job',
}

const PROJECT_LABELS: WorkspaceLabels = {
  mode: 'project',
  entity: 'Project',
  entityPlural: 'Projects',
  entityLower: 'project',
  entityPluralLower: 'projects',
  basePath: '/projects',
  workDaysBasePath: '/projects/workdays',
  workspaceTitle: 'Projects Workspace',
  workDay: 'Work Day',
  workDayPlural: 'Project Work Days',
  workDayLower: 'work day',
  workDayPluralLower: 'work days',
  workDayEntry: 'Work Day Entry',
  workDayEntryPlural: 'Work Day Entries',
  workDayEntryLower: 'work day entry',
  workDayEntryPluralLower: 'work day entries',
  scheduleBadge: 'Project schedule',
  scheduleScopedDescription: 'Pick a date on the calendar, add worker entries, and review the day schedule for this project.',
  workDaysBrowseDescription: 'Browse work days on the calendar first, then select a date to load worker entries for that day.',
  workDayCalendarError: 'Failed to load work day calendar',
  configurationManager: 'Project Configuration Manager',
  unknownEntity: 'Unknown project',
  galleryTitle: 'Project Gallery',
  deleteConfirmationPhrase: 'delete project',
  entityIdLabel: 'Project ID',
  createdEntityLegend: 'Created project',
  recurringFromEntity: 'Generated from recurring project',
}

const EVENT_LABELS: WorkspaceLabels = {
  mode: 'event',
  entity: 'Event',
  entityPlural: 'Events',
  entityLower: 'event',
  entityPluralLower: 'events',
  basePath: '/events',
  workDaysBasePath: '/events/workdays',
  workspaceTitle: 'Events Workspace',
  workDay: 'Event Day',
  workDayPlural: 'Event Days',
  workDayLower: 'event day',
  workDayPluralLower: 'event days',
  workDayEntry: 'Event Day Log',
  workDayEntryPlural: 'Event Day Logs',
  workDayEntryLower: 'event day log',
  workDayEntryPluralLower: 'event day logs',
  scheduleBadge: 'Event schedule',
  scheduleScopedDescription: 'Pick a date on the calendar, add worker entries, and review the day schedule for this event.',
  workDaysBrowseDescription: 'Browse event days on the calendar first, then select a date to load event day logs for that day.',
  workDayCalendarError: 'Failed to load event day calendar',
  configurationManager: 'Event Configuration Manager',
  unknownEntity: 'Unknown event',
  galleryTitle: 'Event Gallery',
  deleteConfirmationPhrase: 'delete event',
  entityIdLabel: 'Event ID',
  createdEntityLegend: 'Created event',
  recurringFromEntity: 'Generated from recurring event',
}

export function getWorkspaceLabels(mode: WorkspacePortalMode): WorkspaceLabels {
  switch (mode) {
    case 'project':
      return PROJECT_LABELS
    case 'event':
      return EVENT_LABELS
    default:
      return JOB_LABELS
  }
}

export function resolveWorkspacePortalMode(
  pathname: string,
  portalMode?: WorkspacePortalMode,
): WorkspacePortalMode {
  if (portalMode) {
    return portalMode
  }

  if (pathname.startsWith('/events')) {
    return 'event'
  }

  if (pathname.startsWith('/projects')) {
    return 'project'
  }

  return 'job'
}

export function isEventSpacePath(pathname: string): boolean {
  return pathname === '/events' || pathname.startsWith('/events/')
}

export function resolveJobKindCode(job: {
  kind_code?: string | null
  is_project?: boolean
}): WorkspacePortalMode {
  const normalized = (job.kind_code ?? '').trim().toLowerCase()
  if (normalized === 'event') {
    return 'event'
  }
  if (normalized === 'project' || job.is_project) {
    return 'project'
  }
  return 'job'
}

export function resolveWorkspaceDetailPath(
  kind: WorkspacePortalMode,
  entityId: string | number,
): string {
  return `${getWorkspaceLabels(kind).basePath}/${entityId}`
}
